import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Dialog,
  DialogContent,
  FormControl,
  FormHelperText,
  ModalProps,
} from "@mui/material";
import { Box } from "@mui/system";
import { Formik } from "formik";
import { useState } from "react";
import * as Yup from "yup";

import AppModalTitle from "@/common/components/Modals/AppModalTitle";
import useMounted from "@/common/hooks/mount/useMounted";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { BaseFormikValues } from "@/common/ts/error";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import { AssignmentDto, AssignmentStage, DamageCostEvaluationDto } from "@/core/api/generated/v1";

import GeneralValidationError from "../../Error/GeneralValidationError";
import AssignmentAutocompleteOrCreate from "../Assignment/AssignmentAutocompleteOrCreate";
import AssignmentLink from "../Assignment/AssignmentLink";

type Props = Pick<ModalProps, "open" | "onClose"> & {
  damageCostEvaluation: DamageCostEvaluationDto;
  onSaved?: (assignment: AssignmentDto) => void;
};

function DamageCostEvaluationAddToAssignmentModal({
  open,
  onClose,
  damageCostEvaluation,
  onSaved,
}: Props) {
  const mounted = useMounted();
  const { enqueueSnackbar } = useAppSnackbar();
  const [isCreatingAssignment, setIsCreatingAssignment] = useState(false);
  const [assignment, setAssignment] = useState<AssignmentDto | undefined>(undefined);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <AppModalTitle onClose={() => onClose && onClose({}, "escapeKeyDown")}>
        Add damage cost evaluation to a assignment
      </AppModalTitle>
      <DialogContent>
        {damageCostEvaluation.assignment && (
          <Alert severity='info' sx={{ mb: 1 }}>
            This damage cost evaluation is already added to the assignment{" "}
            <AssignmentLink entity={damageCostEvaluation.assignment} />
          </Alert>
        )}

        <Box>
          <Formik<
            BaseFormikValues & {
              assignmentId?: string;
            }
          >
            enableReinitialize
            initialValues={{
              assignmentId: damageCostEvaluation.assignment?.id || undefined,
              submit: "",
            }}
            validationSchema={Yup.object().shape({
              // contractId: Yup.string().required("Required"),
            })}
            onSubmit={async (values, { setFieldError, setStatus, setSubmitting }) => {
              try {
                await apiClient.damageCostEvaluationsApi.apiV1DamageCostEvaluationsDamageCostEvaluationIdAddAssignmentPut(
                  {
                    nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                    damageCostEvaluationId: damageCostEvaluation.id!,
                    assignmentAddEntityDto: {
                      assignmentId: values.assignmentId,
                    },
                  },
                );
                enqueueSnackbar("Successfully added.", {
                  variant: "success",
                });

                if (mounted.current) {
                  setStatus({ success: true });
                  setSubmitting(false);
                }

                onSaved && onSaved(assignment!);
                onClose && onClose({}, "escapeKeyDown");
              } catch (err) {
                if (mounted.current) {
                  ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
                  setStatus({ success: false });
                  setSubmitting(false);
                }
              }

              setSubmitting(false);
            }}
          >
            {({ errors, handleSubmit, isSubmitting, values, setFieldValue }) => {
              return (
                // Don't use form here as we using nested form for contract create
                <Box>
                  <FormControl margin='dense' fullWidth error={Boolean(errors.assignmentId)}>
                    <AssignmentAutocompleteOrCreate
                      autocompleteProps={{
                        entityId: values.assignmentId,
                        searchFilters: {
                          vehicleId: damageCostEvaluation?.vehicle?.id || undefined,
                          excludeStage: AssignmentStage.Draft,
                        },
                        isPreload: true,
                        onChange: (newValue) => {
                          setFieldValue(`assignmentId`, newValue?.id);
                          setAssignment(newValue);
                        },
                      }}
                      createUpdateProps={{
                        defaultValues: {
                          vehicleId: damageCostEvaluation?.vehicle?.id || undefined,
                        },
                      }}
                      createFormPlacement='page'
                      onCreateStart={() => setIsCreatingAssignment(true)}
                      onCreate={(newValue) => {
                        setFieldValue(`assignmentId`, newValue?.id);
                        setAssignment(newValue);
                        setIsCreatingAssignment(false);
                      }}
                    />
                    <FormHelperText>{errors.assignmentId}</FormHelperText>
                  </FormControl>

                  <GeneralValidationError sx={{ my: 1 }} errors={errors} />

                  {!isCreatingAssignment && (
                    <LoadingButton
                      sx={{ flex: 1, mt: 1 }}
                      fullWidth
                      color='primary'
                      disabled={!values.assignmentId}
                      loading={isSubmitting}
                      type='button'
                      variant='contained'
                      onClick={() => handleSubmit()}
                    >
                      Save
                    </LoadingButton>
                  )}
                </Box>
              );
            }}
          </Formik>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default DamageCostEvaluationAddToAssignmentModal;
